<script setup lang="ts">
import { FOOTER_LINKS } from './FooterData'
import { airportsOrder, branchesOrder } from '@/data/branchesPageData'

const i18nService = usei18nService()
const { locale } = useI18n()
const localePath = useLocalePath()

const links = FOOTER_LINKS

const CMSLocationsStore = useCMSLocationsStore()
const CMSLocations = ref<ICMSLocationModel[] | undefined>(undefined)

CMSLocations.value = await CMSLocationsStore.fetchCMSLocations()

watch(
	() => CMSLocationsStore.getCMSLocations,
	(newValue) => {
		if (newValue) {
			CMSLocations.value = newValue
		}
	},
	{
		immediate: true,
		deep: true,
	},
)

const getURLforLocation = (item: ICMSLocationModel & { translation_key_title: string }) => {
	const urlLocale = item[`isActive${locale.value.toUpperCase()}`] ? item[`url${locale.value.toUpperCase()}`] : undefined
	const url = item.urlPL

	if (!url && !urlLocale) return undefined
	else
		return urlLocale
			? localePath({ name: 'car-rental-urlSlug', params: { urlSlug: urlLocale } }, locale.value)
			: localePath({ name: 'car-rental-urlSlug', params: { urlSlug: url } }, 'pl')
}

const itemNameLabel = (item: ICMSLocationModel) => {
	return item[`name${locale.value.toUpperCase()}`]
}

const airportsData = computed(() => {
	const airports = CMSLocations.value?.filter(
		(l) => l.icon === 'airplane' && l.showInFooter && !l.otherLocation && !l.deliveryBus && !l.passengerBus && l.isActivePL,
	)

	if (!airports || airports.length === 0) return []

	const sortedAirports = airports.sort((a, b) => {
		const indexA = airportsOrder.indexOf(a.placeId)
		const indexB = airportsOrder.indexOf(b.placeId)
		return (indexA === -1 ? Infinity : indexA) - (indexB === -1 ? Infinity : indexB)
	})

	const formattedAirports = sortedAirports.map((item) => ({
		to: getURLforLocation(item),
		label: itemNameLabel(item),
	}))

	return formattedAirports
})

const branchesData = computed(() => {
	const branches = CMSLocations.value?.filter(
		(l) => l.icon !== 'airplane' && l.showInFooter && !l.otherLocation && !l.deliveryBus && !l.passengerBus && l.isActivePL,
	)

	if (!branches || branches.length === 0) return []

	const sortedBranches = branches.sort((a, b) => {
		const indexA = branchesOrder.indexOf(a.placeId)
		const indexB = branchesOrder.indexOf(b.placeId)
		return (indexA === -1 ? Infinity : indexA) - (indexB === -1 ? Infinity : indexB)
	})

	const formattedBranches = sortedBranches.map((item) => ({
		to: getURLforLocation(item),
		label: itemNameLabel(item),
	}))

	return formattedBranches
})

const otherLocationsData = computed(() => {
	const otherLocations = CMSLocations.value?.filter((l) => l.otherLocation && l.showInFooter && !l.deliveryBus && !l.passengerBus && l.isActivePL)
	if (!otherLocations || otherLocations.length === 0) return []
	const formattedLocations = otherLocations?.map((item) => ({
		to: getURLforLocation(item),
		label: itemNameLabel(item),
	}))
	return formattedLocations?.sort((a, b) => a.label.localeCompare(b.label))
})

const hidden = ref(true)

function show() {
	hidden.value = false
}

function hide() {
	hidden.value = true
}

const footerLinks = (footerArray: any) => {
	if (footerArray.translation_key_title === 'our_branches') {
		return branchesData.value?.length > 0 ? branchesData.value : undefined
	} else if (footerArray.translation_key_title === 'renting_at_the_airport') {
		return airportsData.value?.length > 0 ? airportsData.value : undefined
	} else if (footerArray.translation_key_title === 'onther_locations_in_poland') {
		return otherLocationsData.value?.length > 0 && locale.value !== 'en' ? otherLocationsData.value : undefined
	} else {
		return footerArray.links
	}
}
</script>

<template>
	<Head>
		<meta name="format-detection" content="telephone=no" />
	</Head>
	<footer class="global-footer">
		<div class="global-footer__container">
			<GlobalIcon class="global-footer__logo" name="carfree-logo-white" :width="111" :height="55" />
			<div class="global-footer__info-language-newsletter">
				<div>
					<p class="global-footer__company-name">CarFree Sp. z o.o.</p>
					<p class="global-footer__company-info">{{ $t('components.footer.footer.nip') }}: 521 369 56 26</p>
					<p class="global-footer__company-info">
						{{ $t('components.footer.footer.cybernetyki_5_st') }}<br />02-677 {{ $t('components.footer.footer.warsaw') }}
					</p>
					<div class="global-footer__social">
						<NuxtLink
							class="global-footer__social-item"
							to="https://www.facebook.com/CarFree.Wypozyczalnia.Aut"
							rel="nofollow"
							target="_blank">
							<GlobalIcon name="facebook" :width="20" />
						</NuxtLink>
						<NuxtLink
							class="global-footer__social-item"
							to="https://www.instagram.com/carfree.wypozyczalnia"
							rel="nofollow"
							target="_blank">
							<GlobalIcon name="instagram" :width="20" />
						</NuxtLink>
						<NuxtLink class="global-footer__social-item" to="https://www.tiktok.com/@carfree.pl" rel="nofollow" target="_blank">
							<GlobalIcon name="tiktok" :width="20" />
						</NuxtLink>
					</div>
				</div>
				<!-- TODO: uncomment when newsletter is ready -->
				<div class="global-footer__language-newsletter">
					<GlobalFooterNewsletter />
				</div>
				<!-- <GlobalFooterLanguageSwitcher /> -->
			</div>
			<GlobalFooterContact />
			<GlobalFooterLinksAndApp />
			<div class="global-footer__links-lists-wrapper" :data-hidden="hidden">
				<template v-for="(item, idx) in links" :key="idx">
					<GlobalFooterLinksList
						v-if="footerLinks(item)"
						:title="i18nService.$tDynamic('components.footer.links_and_app.links', item.translation_key_title || item.title)"
						:data="footerLinks(item)" />
				</template>
				<template v-if="otherLocationsData && otherLocationsData?.length > 4">
					<div class="global-footer__gradient"></div>
					<button class="global-footer__btn global-footer__btn--show" @click="show">{{ $t('components.footer.footer.show_more') }}</button>
					<div class="global-footer__btn-container">
						<button class="global-footer__btn" @click="hide">{{ $t('components.footer.footer.show_less') }}</button>
					</div>
				</template>
			</div>
			<GlobalFooterTerms />
		</div>
	</footer>
</template>

<style scoped lang="scss">
@import './Footer';
</style>
